/* ============================================================================
   El directorio, establecimiento por establecimiento.

   Los agregados dicen dónde buscar; esto dice a quién. Es lo que hace falta para
   armar una lista de invitados a una convocatoria.

   Tres decisiones que explican cómo está hecho:

   · Se carga BAJO DEMANDA. Son 23 654 registros, 3.6 MB en crudo, y sólo los
     necesita quien abre esta pantalla. Meterlos al dataset principal habría
     hecho que quien entra a ver el precio de la canasta los descargara también.

   · Se pintan como máximo unos cientos de renglones a la vez. Montar 23 654
     nodos en el DOM congela el navegador varios segundos y nadie lee esa lista
     de corrido: se filtra y se descarga. El conteo real siempre está a la vista,
     para que el recorte no se confunda con el total.

   · NO trae correo ni teléfono, y no es un olvido. Nombre, razón social,
     domicilio y actividad son datos públicos del directorio del INEGI. El correo
     de una persona no lo es de la misma manera, y publicarlo aquí lo volvería
     una lista raspable. La lista con contactos existe fuera del sitio.
   ========================================================================== */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Callout, Card, FilterBar, FilterField, Section, Select } from '../components/ui'
import { integer } from '../lib/format'
import './abasto.css'

/** Orden de campos con el que el pipeline escribe cada renglón. */
interface DirectoryPayload {
  edition: string
  editionDate: string
  fields: string[]
  classes: { code: string; label: string; short: string }[]
  rows: (string | number)[][]
}

interface Establecimiento {
  nombre: string
  razonSocial: string
  clase: string
  estrato: string
  domicilio: string
  colonia: string
  cp: string
  municipio: string
  entidad: string
  lat: number
  lon: number
}

/** Cuántos renglones se pintan. El resto se obtiene con la descarga. */
const MAX_FILAS = 300

/** De menor a mayor, para poder ordenar por tamaño. */
const ORDEN_ESTRATO = [
  '0 a 5 personas',
  '6 a 10 personas',
  '11 a 30 personas',
  '31 a 50 personas',
  '51 a 100 personas',
  '101 a 250 personas',
  '251 y más personas',
]

const TAMANO_OPTIONS = [
  { value: 'todos', label: 'Cualquier tamaño' },
  { value: '11', label: '11 personas o más' },
  { value: '31', label: '31 personas o más' },
  { value: '51', label: '51 personas o más' },
  { value: '101', label: '101 personas o más' },
]

const MINIMO: Record<string, number> = { todos: 0, '11': 2, '31': 3, '51': 4, '101': 5 }

/* Unos pocos registros del DENUE llegan sin nombre comercial NI razón social.
   Sin esto la celda encabezado del renglón queda vacía: en pantalla es un hueco y
   en un lector de pantalla es una fila sin identificar. El domicilio es lo que
   queda para reconocerla. */
const rotulo = (e: Establecimiento): string =>
  e.nombre || e.razonSocial || (e.domicilio ? `(sin nombre) ${e.domicilio}` : '(sin nombre)')

const normalizar = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

export function AbastoDirectorio({
  claseActiva,
  estados,
}: {
  /** Clase SCIAN seleccionada arriba, o `todas`. */
  claseActiva: string
  /** Catálogo de entidades, para traducir la clave a nombre. */
  estados: { stateCode: string; stateName: string }[]
}) {
  const [payload, setPayload] = useState<DirectoryPayload | null>(null)
  const [estado, setEstado] = useState<'inicial' | 'cargando' | 'listo' | 'error'>('inicial')
  const [entidad, setEntidad] = useState('todas')
  const [tamano, setTamano] = useState('todos')
  const [busqueda, setBusqueda] = useState('')
  const pedido = useRef(false)

  useEffect(() => {
    if (pedido.current) return
    pedido.current = true
    setEstado('cargando')
    /* `base` relativa: el sitio puede servirse desde un subdirectorio. */
    fetch(`${import.meta.env.BASE_URL}mayoristas.json`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json() as Promise<DirectoryPayload>
      })
      .then((data) => {
        setPayload(data)
        setEstado('listo')
      })
      .catch(() => setEstado('error'))
  }, [])

  const nombreEntidad = useMemo(
    () => new Map(estados.map((e) => [e.stateCode, e.stateName])),
    [estados],
  )

  const registros = useMemo<Establecimiento[]>(() => {
    if (!payload) return []
    return payload.rows.map((r) => ({
      nombre: String(r[0] ?? ''),
      razonSocial: String(r[1] ?? ''),
      clase: String(r[2] ?? ''),
      estrato: String(r[3] ?? ''),
      domicilio: String(r[4] ?? ''),
      colonia: String(r[5] ?? ''),
      cp: String(r[6] ?? ''),
      municipio: String(r[7] ?? ''),
      entidad: String(r[8] ?? ''),
      lat: Number(r[9] ?? 0),
      lon: Number(r[10] ?? 0),
    }))
  }, [payload])

  /* El índice de búsqueda se calcula una vez, no en cada tecla: normalizar
     23 654 cadenas en cada pulsación hacía que el campo se sintiera pegajoso.

     Incluye TODO lo que la tabla imprime, no sólo el nombre: quien ve «Central de
     Abasto, Iztapalapa» en pantalla y lo teclea espera encontrarlo, y con un
     índice de tres campos la búsqueda por colonia o por estado devolvía cero. */
  const indice = useMemo(
    () =>
      registros.map((e) =>
        normalizar(
          `${e.nombre} ${e.razonSocial} ${e.domicilio} ${e.colonia} ${e.cp} ${e.municipio} ${
            nombreEntidad.get(e.entidad) ?? ''
          }`,
        ),
      ),
    [registros, nombreEntidad],
  )

  const filtrados = useMemo(() => {
    const q = normalizar(busqueda.trim())
    const minimo = MINIMO[tamano] ?? 0
    const out: Establecimiento[] = []
    for (let i = 0; i < registros.length; i++) {
      const e = registros[i]!
      if (claseActiva !== 'todas' && e.clase !== claseActiva) continue
      if (entidad !== 'todas' && e.entidad !== entidad) continue
      if (minimo > 0 && ORDEN_ESTRATO.indexOf(e.estrato) < minimo) continue
      if (q && !indice[i]!.includes(q)) continue
      out.push(e)
    }
    /* Del más grande al más chico: para una convocatoria, el orden útil es por
       capacidad plausible, no alfabético. */
    return out.sort((a, b) => {
      const d = ORDEN_ESTRATO.indexOf(b.estrato) - ORDEN_ESTRATO.indexOf(a.estrato)
      return d !== 0 ? d : rotulo(a).localeCompare(rotulo(b), 'es-MX')
    })
  }, [registros, indice, claseActiva, entidad, tamano, busqueda])

  const entidadOptions = useMemo(
    () => [
      { value: 'todas', label: 'Todo el país' },
      ...[...nombreEntidad.entries()]
        .map(([code, name]) => ({ value: code, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es-MX')),
    ],
    [nombreEntidad],
  )

  const claseDe = useMemo(
    () => new Map((payload?.classes ?? []).map((c) => [c.code, c.short])),
    [payload],
  )

  function descargar(): void {
    const esc = (v: string): string => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)
    const head = [
      'nombre','razon_social','clase','scian','tamano','domicilio','colonia',
      'codigo_postal','municipio','entidad','latitud','longitud',
    ]
    const filas = filtrados.map((e) => [
      rotulo(e), e.razonSocial, claseDe.get(e.clase) ?? '', e.clase, e.estrato,
      e.domicilio, e.colonia, e.cp, e.municipio, nombreEntidad.get(e.entidad) ?? e.entidad,
      String(e.lat), String(e.lon),
    ])
    /* BOM al inicio: sin él, Excel en Windows abre el CSV en latin-1 y destroza
       cada acento del país. */
    const csv = `\uFEFF${[head, ...filas].map((r) => r.map(esc).join(',')).join('\r\n')}\r\n`
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `mayoristas-${filtrados.length}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (estado === 'error') {
    return (
      <Section title="Directorio" description="Los establecimientos, uno por uno.">
        <Callout kind="warning" title="No se pudo cargar el directorio">
          <p>
            El archivo <code>mayoristas.json</code> no respondió. Los agregados de arriba sí están:
            vienen del dataset principal y no dependen de esta descarga.
          </p>
        </Callout>
      </Section>
    )
  }

  return (
    <Section
      title="Directorio"
      description="Los establecimientos uno por uno, del más grande al más chico. Filtra, revisa y descarga la lista para trabajarla."
    >
      <Card>
        {estado !== 'listo' ? (
          <p className="t-footnote ink-3" role="status">
            Cargando el directorio…
          </p>
        ) : (
          <div className="stack">
            <FilterBar ariaLabel="Filtros del directorio">
              <FilterField label="Entidad">
                <Select
                  value={entidad}
                  onChange={setEntidad}
                  options={entidadOptions}
                  ariaLabel="Entidad federativa"
                />
              </FilterField>
              <FilterField label="Tamaño">
                <Select
                  value={tamano}
                  onChange={setTamano}
                  options={TAMANO_OPTIONS}
                  ariaLabel="Tamaño del establecimiento"
                />
              </FilterField>
              <FilterField label="Buscar">
                <input
                  type="search"
                  className="dir__search t-callout"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Nombre, domicilio, colonia, municipio…"
                  aria-label="Buscar en nombre, razón social, domicilio, colonia, código postal, municipio o entidad"
                />
              </FilterField>
            </FilterBar>

            <div className="row row--between dir__bar">
              <p className="t-footnote ink-2" role="status">
                {integer(filtrados.length)}{' '}
                {filtrados.length === 1 ? 'establecimiento' : 'establecimientos'}
                {filtrados.length > MAX_FILAS
                  ? ` · se muestran los primeros ${integer(MAX_FILAS)}, la descarga los trae todos`
                  : ''}
              </p>
              <button
                type="button"
                className="dir__download t-footnote"
                onClick={descargar}
                disabled={filtrados.length === 0}
              >
                Descargar CSV ({integer(filtrados.length)})
              </button>
            </div>

            {filtrados.length === 0 ? (
              <p className="t-footnote ink-3">
                Ningún establecimiento cumple estos filtros. Prueba con un tamaño menor o quita la
                búsqueda.
              </p>
            ) : (
              <div className="dir__scroll">
                <table className="dir__table" role="table">
                  <caption className="sr-only">
                    Establecimientos mayoristas de alimento, con su razón social, tamaño y domicilio.
                  </caption>
                  <thead role="rowgroup">
                    <tr role="row">
                      <th scope="col" role="columnheader" className="t-micro ink-3">Establecimiento</th>
                      <th scope="col" role="columnheader" className="t-micro ink-3">Qué vende</th>
                      <th scope="col" role="columnheader" className="t-micro ink-3">Tamaño</th>
                      <th scope="col" role="columnheader" className="t-micro ink-3">Dónde</th>
                    </tr>
                  </thead>
                  <tbody role="rowgroup">
                    {filtrados.slice(0, MAX_FILAS).map((e, i) => (
                      <tr key={`${e.nombre}-${e.cp}-${i}`} className="dir__row" role="row">
                        <th scope="row" role="rowheader" className="dir__name">
                          <span className="t-callout">{rotulo(e)}</span>
                          {e.nombre && e.razonSocial && normalizar(e.razonSocial) !== normalizar(e.nombre) ? (
                            <span className="t-caption ink-3">{e.razonSocial}</span>
                          ) : null}
                        </th>
                        <td role="cell" className="t-caption ink-2" data-rotulo="Qué vende">
                          {claseDe.get(e.clase) ?? e.clase}
                        </td>
                        <td role="cell" className="t-caption ink-2 dir__size" data-rotulo="Tamaño">
                          {e.estrato}
                        </td>
                        <td role="cell" className="t-caption ink-2" data-rotulo="Dónde">
                          <span className="dir__where">
                            {[e.domicilio, e.colonia].filter(Boolean).join(', ')}
                          </span>
                          <span className="t-caption ink-3 dir__place">
                            {e.municipio}, {nombreEntidad.get(e.entidad) ?? e.entidad}
                            {e.cp ? ` · CP ${e.cp}` : ''}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* El mismo aviso que arriba, repetido abajo a propósito: la tabla se
                desplaza dentro de su caja, así que quien llega al renglón 300
                dejó de ver el aviso de arriba hace rato y no tiene por qué saber
                que la lista sigue. */}
            {filtrados.length > MAX_FILAS ? (
              <p className="t-footnote ink-3 dir__mas">
                Hay {integer(filtrados.length - MAX_FILAS)} establecimientos más que no caben en la
                tabla. Afina los filtros o descarga el CSV: trae los {integer(filtrados.length)}.
              </p>
            ) : null}
          </div>
        )}
      </Card>
    </Section>
  )
}
